import { DateTime } from 'luxon';
import {
  type GqlCompany,
  type GqlCompleteShareCapitalChange,
  type GqlShareTransactionGroup,
  GqlTransactionKind,
} from '../../../../graphql/base-types.graphql';
import { type Control, useWatch } from 'react-hook-form';
import { type StgFormData } from '../TransactionGroup/TransactionGroupForm';
import { isNil } from 'lodash-es';

export const useDateRange = (
  control: Control<StgFormData>,
  stgs: Pick<GqlShareTransactionGroup, 'transactionDate' | 'sortOrder' | 'kind' | 'snrShareCapitalChangeId'>[],
  prevSnrScc: Pick<GqlCompleteShareCapitalChange, 'id' | 'decisionDate' | 'registrationDate'> | null | undefined,
  nextSnrScc:
    | Pick<GqlCompleteShareCapitalChange, 'id' | 'decisionDate' | 'registrationDate' | 'kind'>
    | null
    | undefined,
  company: Pick<GqlCompany, 'foundationDate'>,
): { minDate: DateTime | undefined; maxDate?: DateTime | undefined } => {
  const selectedKind = useWatch({ control, name: 'kind' });
  return getDateRange(selectedKind, stgs, prevSnrScc, nextSnrScc, company);
};

export const getDateRange = (
  selectedKind: GqlTransactionKind | 'next',
  stgs: Pick<GqlShareTransactionGroup, 'transactionDate' | 'sortOrder' | 'kind' | 'snrShareCapitalChangeId'>[],
  prevSnrScc: Pick<GqlCompleteShareCapitalChange, 'id' | 'decisionDate' | 'registrationDate'> | null | undefined,
  nextSnrScc:
    | Pick<GqlCompleteShareCapitalChange, 'id' | 'decisionDate' | 'registrationDate' | 'kind'>
    | null
    | undefined,
  company: Pick<GqlCompany, 'foundationDate'>,
) => {
  const currentKind =
    selectedKind === 'next'
      ? /* if the next scc doesnt exist, it has to be a split/merge*/ (nextSnrScc?.kind ?? GqlTransactionKind.Split)
      : selectedKind;

  switch (currentKind) {
    case GqlTransactionKind.Issue:
    case GqlTransactionKind.Unissue:
    case GqlTransactionKind.Split:
    case GqlTransactionKind.Merge: {
      // These must always be added in the order they come in;
      // We check prevSnrScc because it might be that the first one isn't fully allocated
      if (stgs.length === 0 || isNil(prevSnrScc)) {
        if (!nextSnrScc?.registrationDate) {
          throw new Error('missing nextSnrScc.registrationDate');
        }
        return {
          minDate: DateTime.fromISO(company.foundationDate),
          maxDate: DateTime.fromISO(nextSnrScc.registrationDate),
        };
      }

      const lastStg = stgs.findLast(
        stg => stg.snrShareCapitalChangeId && prevSnrScc?.id.startsWith(stg.snrShareCapitalChangeId),
      );
      if (!lastStg) {
        throw new Error('could not find lastStg');
      }
      const nextStg = stgs.find(stg => stg.snrShareCapitalChangeId && stg.snrShareCapitalChangeId === nextSnrScc?.id);
      const maxDate = nextStg?.transactionDate ?? nextSnrScc?.registrationDate;
      return {
        minDate: DateTime.fromISO(lastStg.transactionDate),
        maxDate: maxDate ? DateTime.fromISO(maxDate) : undefined,
      };
    }
    case GqlTransactionKind.Transfer:
    case GqlTransactionKind.BlockPartition:
    case GqlTransactionKind.CurrencyChange:
    case GqlTransactionKind.ShareClassConvert: {
      // These can be added anywhere between the first stg and the future
      const firstStg = stgs[0];

      if (!firstStg) {
        throw new Error('there should at least be one stg');
      }
      return { minDate: DateTime.fromISO(firstStg.transactionDate) };
    }

    default: {
      throw new Error(`case for ${currentKind} not implemented`);
    }
  }
};
